import { useUploadCaseNoteAttachmentMutation } from '@/store/api/services/operations/cases/notes';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import toast from 'react-hot-toast';

interface CaseNoteUploadAttachmentProps extends ModalChildrenProps {
  case_id: string;
  case_note_id: string;
}

const CaseNoteUploadAttachment = ({ hide, props }: CaseNoteUploadAttachmentProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadAttachment, { isLoading }] = useUploadCaseNoteAttachmentMutation();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFiles(Array.from(files));
    }
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    if (!props?.case_id || !props?.case_note_id) {
      toast.error('Case ID and Note ID are required');
      return;
    }

    try {
      await uploadAttachment({
        case_id: props.case_id,
        case_note_id: props.case_note_id,
        files: selectedFiles,
      }).unwrap();

      toast.success(
        selectedFiles.length === 1
          ? 'Attachment uploaded successfully'
          : `${selectedFiles.length} attachments uploaded successfully`,
      );
      hide();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to upload attachment');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upload Attachment to Note
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Upload documents, images, or other files to attach to this case note.
        </Alert>

        <Box
          sx={{
            border: '2px dashed',
            borderColor: selectedFiles.length > 0 ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            backgroundColor: selectedFiles.length > 0 ? 'action.hover' : 'background.paper',
            transition: 'all 0.3s',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            id="file-input"
            type="file"
            hidden
            multiple
            onChange={handleFileChange}
            accept="*/*"
          />

          {selectedFiles.length > 0 ? (
            <Stack spacing={2} alignItems="center">
              <AttachFileIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="body1" fontWeight="500">
                {selectedFiles.length === 1
                  ? selectedFiles[0].name
                  : `${selectedFiles.length} files selected`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedFiles.length === 1
                  ? formatFileSize(selectedFiles[0].size)
                  : `Total: ${formatFileSize(selectedFiles.reduce((acc, f) => acc + f.size, 0))}`}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFiles([]);
                }}
              >
                Remove {selectedFiles.length > 1 ? 'Files' : 'File'}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2} alignItems="center">
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant="body1" fontWeight="500">
                Click to select files
              </Typography>
              <Typography variant="caption" color="text.secondary">
                or drag and drop
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Maximum file size: 50 MB per file
              </Typography>
            </Stack>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button onClick={hide} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={selectedFiles.length === 0 || isLoading}
        >
          {isLoading
            ? 'Uploading...'
            : `Upload ${selectedFiles.length > 1 ? 'Attachments' : 'Attachment'}`}
        </Button>
      </DialogActions>
    </>
  );
};

export default CaseNoteUploadAttachment;
